import { useEffect, useRef, useState } from "react";
import { animate } from "motion";
import { cn } from "@/lib/utils";
import type { CtaConfig } from "@/lib/end-point-engine";
import tipTexture from "@/assets/end-point/tip.png";
import fallbackImage from "@/assets/end-point/fallback.png";

// The live website's values (dolfinai.co `lib/cta/config.ts`).
const config: CtaConfig = {
  dispersion: 0.8,
  dispersionBand: 0.9,
  dispersionOffset: 0.385,
  highlightRadius: 2,
  highlightSpeed: 8,
  highlightStrength: 2,
  introProgress: 0,
  longLengthMax: 8,
  longLengthMin: 3,
  longSegments: 96,
  maxStart: { x: 1, y: -0.6, z: 1 },
  minStart: { x: -1, y: 0.6, z: 0 },
  noiseScale: 0.73,
  noiseStrength: 0.35,
  pulseSpeedBase: 0.03,
  pulseSpeedRandomMax: 0.1,
  pulseStrength: 0.35,
  shortLengthMax: 4,
  shortLengthMin: 1.4,
  shortLineRatio: 0.9,
  shortSegments: 48,
  simDamping: 6,
  simMaxVelocity: 14,
  simRadius: 2,
  simSpring: 45,
  simStrength: 15,
  sizeVariance: 0.5,
  springSettings: { bounce: 0.2, duration: 1200 },
  strandOpacity: 0.25,
  tipLayers: 6,
  tipRadius: 0.02,
  tipTexture,
  totalLines: 1600,
  tubeRadius: 0.01,
};

/** three.js loads lazily; falls back to a still frame under reduced motion or without WebGL. Inverted in light mode. */
export function EndPointBurst({ className }: { className?: string }) {
  const mount = useRef<HTMLDivElement>(null);
  const [fallback, setFallback] = useState(false);

  useEffect(() => {
    const el = mount.current;
    if (!el) return;
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    let disposed = false;
    let generation = 0;
    let dispose: (() => void) | undefined;
    let reveal: ReturnType<typeof animate> | undefined;
    const cfg = { ...config };

    const stop = () => {
      generation++;
      el.querySelector("canvas")?.removeEventListener("webglcontextlost", onContextLost);
      reveal?.stop();
      dispose?.();
      dispose = undefined;
    };
    const fail = () => {
      if (disposed) return;
      stop();
      setFallback(true);
    };
    const onContextLost = (event: Event) => {
      event.preventDefault();
      fail();
    };
    const start = async () => {
      const request = ++generation;
      cfg.introProgress = 0;
      try {
        const { createScene } = await import("@/lib/end-point-engine");
        if (disposed || preference.matches || request !== generation) return;
        dispose = createScene(el, cfg);
        if (!dispose) return fail();
        el.querySelector("canvas")?.addEventListener("webglcontextlost", onContextLost, { once: true });
        reveal = animate(0, 1, {
          type: "spring",
          duration: 1.2,
          bounce: 0.2,
          onUpdate: (value) => {
            cfg.introProgress = value;
          },
        });
      } catch {
        fail();
      }
    };
    const updatePreference = () => {
      if (preference.matches) return fail();
      setFallback(false);
      void start();
    };

    if (preference.matches) setFallback(true);
    else void start();
    preference.addEventListener("change", updatePreference);
    return () => {
      disposed = true;
      preference.removeEventListener("change", updatePreference);
      stop();
    };
  }, []);

  return (
    <div aria-hidden="true" className={cn("overflow-hidden invert dark:invert-0", className)}>
      <div ref={mount} className="absolute inset-0" />
      {fallback && <img src={fallbackImage} alt="" className="absolute inset-0 size-full object-cover" />}
    </div>
  );
}
